import {Component, OnDestroy, OnInit, HostListener, ChangeDetectorRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {Update} from '@ngrx/entity';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
    AssignmentModel, AssignmentOnServerCreated, AssignmentUpdated, selectAssignmentById,
    selectAssignmentsActionLoading, selectLastCreatedAssignmentId
} from '../../../../../core/assignment';
import {AllUsersOfRoleRequested, selectValuersInStore, User} from '../../../../../core/auth';
import {AgencyModel, AllAgenciesRequested, selectAllAgencies} from '../../../../../core/admin';
import {LayoutConfigService, SubheaderService} from '../../../../../core/_base/layout';
import {LayoutUtilsService, MessageType, TypesUtilsService} from '../../../../../core/_base/crud';
import {AppState} from '../../../../../core/reducers';
import { delay } from 'rxjs/operators';

@Component({
    selector: 'kt-assignment-edit',
    templateUrl: './assignment-edit.component.html',
    styleUrls: ['./assignment-edit.component.scss'],
})


export class AssignmentEditComponent implements OnInit, OnDestroy {
// Public properties
    assignment: AssignmentModel;
    isExistAss = new BehaviorSubject<boolean>(false);
    oldAssignment: AssignmentModel;
    assignmentForm: UntypedFormGroup;

    valuers: User[] = [];
    agencies: AgencyModel[] = [];

    hasFormErrors = false;
    saveState = false;
    filteredOptions: Observable<User[]>;

    loading$: Observable<boolean>;
    // Private properties
    private subscriptions: Subscription[] = [];
    readonly = false;

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param subheaderService: SubheaderService
     * @param typesUtilsService: TypesUtilsService
     * @param layoutUtilsService
     * @param store
     * @param ref
     * @param layoutConfigService
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private subheaderService: SubheaderService,
                public typesUtilsService: TypesUtilsService,
                private layoutUtilsService: LayoutUtilsService,
                private store: Store<AppState>,
                private ref: ChangeDetectorRef,
                private layoutConfigService: LayoutConfigService,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectAssignmentsActionLoading));

        const routeSubscription = this.activatedRoute.params.subscribe(params => {
            const id = params.id;
            if (id && id > 0) {
                const assSubScription = this.store.pipe(select(selectAssignmentById(id))).subscribe(res => {
                    if (res) {
                        this.oldAssignment = Object.assign({}, res);
                        this.assignment = Object.assign({}, this.oldAssignment);
                        this.readonly = this.assignment.status == 2;
                        this.isExistAss.next(true);
                        this.createForm();
                    }
                });
                this.subscriptions.push(assSubScription);
            } else {
                this.oldAssignment = new AssignmentModel();
                this.oldAssignment.clear();
                this.assignment = Object.assign({}, this.oldAssignment);
                this.isExistAss.next(true);
                this.createForm();
            }
        });
        this.subscriptions.push(routeSubscription);

        this.store.dispatch(new AllUsersOfRoleRequested({roleId: 2}));
        const userSubscription = this.store.pipe(
            select(selectValuersInStore))
            .subscribe(res => {
                this.valuers = [];
                if (res) {
                    this.valuers = res.items.filter(item => item.qualification_id != null && item.qualification_id != undefined);
                    this.ref.detectChanges();
                }
            });
        this.subscriptions.push(userSubscription);


        this.store.dispatch(new AllAgenciesRequested());
        const agencySubscription = this.store.pipe(
            select(selectAllAgencies))
            .subscribe(res => {
                this.agencies = [];
                if (res) {
                    this.agencies = res;
                    this.ref.detectChanges();
                }
            });
        this.subscriptions.push(agencySubscription);

    }

    ngOnDestroy(): void {

        this.subscriptions.forEach(_subscription => {
            _subscription.unsubscribe();
        });
    }

    createForm() {
        this.assignmentForm = this.fb.group({
            name: [this.assignment.name, Validators.required],
            valuer_id: [this.assignment.valuer_id, Validators.required],
            agency_id: [this.assignment.agency_id, Validators.required],
            pointOfContact: [this.assignment.pointOfContact, Validators.required],
            description: [this.assignment.description, Validators.required],
            insertion_date: [{
                value: this.typesUtilsService.getDateFromString(this.assignment.insertion_date),
                disabled: true
            }, Validators.required]
        });

        if (this.assignment.id) {
            this.subheaderService.setTitle('Add Assignment');
            this.subheaderService.setBreadcrumbs([
                {title: 'Main menu', page: `../default/assignment`},
                {title: 'Assignments', page: `../default/assignment/assignments`},
                {title: 'Edit assignment', page: `../default/assignment/assignments/edit`}
            ]);
            return;
        }
        this.subheaderService.setTitle('Edit Assignment');
        this.subheaderService.setBreadcrumbs([
            {title: 'Main menu', page: `../default/assignment`},
            {title: 'Assignments', page: `../default/assignment/assignments`},
            {title: 'Add assignment', page: `../default/assignment/assignments/add`}
        ]);
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.assignment && this.assignment.id) {
            // return `Edit assignment "${this.assignment.name}"`;
            return this.translate.instant('ASSIGNMENT.FORM.TITLE.EDIT', {
                name: this.assignment.name
            });
        }
        return this.translate.instant('ASSIGNMENT.FORM.TITLE.NEW');
    }

    back() {
        this.location.back();
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.assignmentForm.controls[controlName];
        return control.invalid && control.touched;
    }

    /** ACTIONS */

    /**
     * Returns prepared assignment
     */
    prepareAssignment(): AssignmentModel {
        const controls = this.assignmentForm.controls;
        const _assignment = new AssignmentModel();
        _assignment.clear();

        // set value
        _assignment.id = this.assignment.id;
        const _date = controls.insertion_date.value;
        if (_date) {
            _assignment.insertion_date = this.typesUtilsService.dateFormat(_date);
        }
        _assignment.name = controls.name.value;
        _assignment.valuer_id = controls.valuer_id.value;
        _assignment.agency_id = controls.agency_id.value;

        _assignment.pointOfContact = controls.pointOfContact.value;
        _assignment.description = controls.description.value;

        return _assignment;
    }

    /**     valid Form      **/
    isFormValid() {

        if (!this.assignmentForm) {
            return false;
        }
        const controls = this.assignmentForm.controls;
        const name = controls.name.value;
        const valuerId = controls.valuer_id.value;
        const agencyId = controls.agency_id.value;
        const _date = controls.insertion_date.value;

        return _date &&
            name && name.length > 0 &&
            valuerId &&
            agencyId;
    }

    /**
     * On Submit
     */
    onSubmit() {

        this.hasFormErrors = false;
        const controls = this.assignmentForm.controls;
        /** check form */
        if (this.assignmentForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        const editedAssignment = this.prepareAssignment();
        if (editedAssignment.id > 0) {
            this.updateAssignment(editedAssignment);
        } else {
            this.createAssignment(editedAssignment);
        }
    }

    /**
     * Update assignment
     *
     * @param _assignment: AssignmentModel
     */
    updateAssignment(_assignment: AssignmentModel) {
        const updateAssignment: Update<AssignmentModel> = {
            id: _assignment.id,
            changes: _assignment
        };
        this.store.dispatch(new AssignmentUpdated({
            partialAssignment: updateAssignment,
            assignment: _assignment
        }));
        this.saveState = true;
        this.oldAssignment = _assignment;
        this.layoutUtilsService.showActionNotification(
            this.translate.instant('GENERAL.MESSAGE.SAVE_CHANGES'), 
            MessageType.Update, 3000, true, false
        );
        
        this.router.navigate(['../../', this.oldAssignment.id, 'toes'], {relativeTo: this.activatedRoute});
    }

    /**
     * Create assignment
     *
     * @param _assignment: AssignmentModel
     */
    createAssignment(_assignment: AssignmentModel) {
        this.store.dispatch(new AssignmentOnServerCreated({assignment: _assignment}));
        this.oldAssignment = _assignment;
        const _subScription = this.store.pipe(
            select(selectLastCreatedAssignmentId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.saveState = true;
            this.router.navigate(['..', res, 'toes'], {relativeTo: this.activatedRoute});
        });
        this.subscriptions.push(_subScription);
    }

    /**
     * Reset assignment
     */
    reset() {
        this.assignment = Object.assign({}, this.oldAssignment);
        this.createForm();
        this.hasFormErrors = false;
        this.assignmentForm.markAsPristine();
        this.assignmentForm.markAsUntouched();
        this.assignmentForm.updateValueAndValidity();
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        const assignment = this.prepareAssignment();
        if (!this.saveState && (assignment.name != this.oldAssignment.name ||
                assignment.valuer_id != this.oldAssignment.valuer_id ||
                assignment.agency_id != this.oldAssignment.agency_id ||
                assignment.pointOfContact != this.oldAssignment.pointOfContact ||
                assignment.description != this.oldAssignment.description)) {
            return true;
        } else {
            return false;
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHander($event) {
        if (this.discard()) {
            $event.preventDefault();
            $event.returnValue = 'test';
        }
    }

}
